import React, { useState } from 'react'
import { useLocation } from 'wouter'

import { buildComponent } from '../../../../components/factory'
import { graphql } from '../../../../services/graphql'
import { Category } from '../../../../state/models/audio'
import { useStoreState } from '../../../../hooks/state'

import { useForm } from '@mantine/form'

import * as UI from '@mantine/core'
import * as Icons from '../../../../components/icons'
import * as Semantics from '../../../../components/semantics'

const typeOptions = [
  { value: 'theme', label: 'Thématique' },
  { value: 'book', label: 'Audiobook' },
  { value: 'background', label: 'Fond sonore' },
]

export const SectionMain = buildComponent()
  .withLifecycle(() => {
    const [, setLocation] = useLocation()
    const [submitting, setSubmitting] = useState(false)

    const allCategories = useStoreState((store) => store.audio.categories)

    const form = useForm({
      initialValues: {
        titleFr: '',
        titleEn: '',
        subtitleFr: '',
        subtitleEn: '',
        type: 'theme' as 'theme' | 'book' | 'background',
        parent: '',
      },
      validate: {
        titleFr: (value) => (value.trim() ? null : 'Titre français requis'),
        titleEn: (value) => (value.trim() ? null : 'Titre anglais requis'),
        subtitleFr: (value: string) =>
          value.trim() ? null : 'Sous-titre français requis',
        subtitleEn: (value: string) =>
          value.trim() ? null : 'Sous-titre anglais requis',
        type: (value: string) => (value ? null : 'Type requis'),
      },
    })

    // Fonction pour filtrer les parents disponibles avec protection anti-cyclique
    const getAvailableParents = () => {
      if (!form.values.type) {
        return []
      }

      return allCategories
        .filter((cat: Category) => {
          // Ne montrer que les catégories du même type
          if (cat.type !== form.values.type) {
            return false
          }

          // Pour la création, on ne peut pas sélectionner une catégorie qui a déjà un parent
          // car cela créerait une hiérarchie à 3 niveaux ou plus
          if (cat.parent) {
            return false
          }

          return true
        })
        .map((cat: Category) => ({
          value: cat._id,
          label: cat.short?.fr || cat.short?.en || cat.slug,
        }))
    }

    const submit = form.onSubmit(async (values) => {
      try {
        setSubmitting(true)
        form.clearErrors()

        // Générer le slug à partir du français d'abord, puis anglais si français vide
        const baseTitle = values.titleFr.trim() || values.titleEn.trim()
        const slug = baseTitle
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '')

        const createData = {
          slug,
          short: {
            fr: values.titleFr,
            en: values.titleEn,
          },
          long: {
            fr: values.subtitleFr,
            en: values.subtitleEn,
          },
          image: 'default',
          type: values.type,
          parent: values.parent || undefined,
        }

        const result = await graphql.mutations.createCategory(createData)
        setLocation(`/categories/edit/${result.categoryCreate._id}`)
      } catch (err: any) {
        if ('message' in err) {
          if (err.message.includes('duplicate key')) {
            return form.setFieldError(
              'titleFr',
              'Ce titre est déjà utilisé par une autre catégorie',
            )
          }
        }

        form.setFieldError(
          'titleFr',
          'Une erreur est survenue lors de la création',
        )
      } finally {
        setSubmitting(false)
      }
    })

    return {
      form,
      submit,
      submitting,
      allCategories,
      getAvailableParents,
    }
  })
  .withRender(({ lifecycle }) => (
    <form onSubmit={lifecycle.submit}>
      <UI.Stack spacing={32}>
        <UI.Stack spacing={24}>
          <UI.Input.Wrapper
            label={<b style={{ fontSize: '2rem' }}>Titre de la catégorie</b>}
            description="Nom court de la catégorie affiché dans l'interface"
          >
            <Semantics.LocalizedInput
              value={{
                fr: lifecycle.form.values.titleFr,
                en: lifecycle.form.values.titleEn,
              }}
              onChange={(value) => {
                lifecycle.form.setFieldValue('titleFr', value.fr)
                lifecycle.form.setFieldValue('titleEn', value.en)
              }}
            />
            {lifecycle.form.errors.titleFr && (
              <UI.Text color="red" size="sm">
                {lifecycle.form.errors.titleFr}
              </UI.Text>
            )}
            {lifecycle.form.errors.titleEn && (
              <UI.Text color="red" size="sm">
                {lifecycle.form.errors.titleEn}
              </UI.Text>
            )}
          </UI.Input.Wrapper>

          <UI.Input.Wrapper
            label={<b style={{ fontSize: '2rem' }}>Sous-titre</b>}
            description="Sous-titre de la catégorie pour donner plus de contexte"
          >
            <Semantics.LocalizedInput
              value={{
                fr: lifecycle.form.values.subtitleFr,
                en: lifecycle.form.values.subtitleEn,
              }}
              onChange={(value) => {
                lifecycle.form.setFieldValue('subtitleFr', value.fr)
                lifecycle.form.setFieldValue('subtitleEn', value.en)
              }}
            />
            {lifecycle.form.errors.subtitleFr && (
              <UI.Text color="red" size="sm">
                {lifecycle.form.errors.subtitleFr}
              </UI.Text>
            )}
            {lifecycle.form.errors.subtitleEn && (
              <UI.Text color="red" size="sm">
                {lifecycle.form.errors.subtitleEn}
              </UI.Text>
            )}
          </UI.Input.Wrapper>

          <UI.Stack spacing="md">
            <UI.Select
              label="Type"
              placeholder="Sélectionnez un type"
              required
              data={typeOptions}
              {...lifecycle.form.getInputProps('type')}
            />

            <UI.Input.Wrapper
              label={<b style={{ fontSize: '2rem' }}>Catégorie parente</b>}
              description={
                !lifecycle.form.values.type
                  ? "Veuillez d'abord sélectionner un type pour cette catégorie"
                  : 'Catégorie parente pour organiser hiérarchiquement (optionnel)'
              }
            >
              <UI.Select
                mt={8}
                placeholder={
                  !lifecycle.form.values.type
                    ? "Sélectionnez d'abord un type"
                    : 'Aucune catégorie parente'
                }
                disabled={!lifecycle.form.values.type}
                clearable
                data={lifecycle.getAvailableParents()}
                {...lifecycle.form.getInputProps('parent')}
              />
            </UI.Input.Wrapper>
          </UI.Stack>
        </UI.Stack>

        <UI.Divider />

        <UI.Group position="right" spacing="sm">
          <UI.Button variant="subtle" component="a" href="/categories">
            Annuler
          </UI.Button>
          <UI.Button
            type="submit"
            loading={lifecycle.submitting}
            leftIcon={<Icons.FontAwesomeIcon icon={Icons.Solid.faCheck} />}
          >
            Créer la catégorie
          </UI.Button>
        </UI.Group>
      </UI.Stack>
    </form>
  ))
